import { Link2, TrendingUp } from "lucide-react"

interface Correlation {
  tag: string
  symptom_type: string
  occurrences: number
  total: number
  percentage: number
  strength: string
}

interface CorrelationListProps {
  correlations: Correlation[]
  heroCorrelation: Correlation | null
}

function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

function getStrengthStyle(strength: string): {
  bg: string
  color: string
} {
  switch (strength) {
    case "strong":
      return {
        bg: "var(--fig-correlation-strong-bg)",
        color: "var(--fig-correlation-strong)",
      }
    case "moderate":
      return {
        bg: "var(--fig-correlation-moderate-bg)",
        color: "var(--fig-correlation-moderate)",
      }
    default:
      return {
        bg: "var(--fig-correlation-possible-bg)",
        color: "var(--fig-correlation-possible)",
      }
  }
}

export function CorrelationList({
  correlations,
  heroCorrelation,
}: CorrelationListProps) {
  // Filter out the hero correlation (already shown separately)
  const remaining = heroCorrelation
    ? correlations.filter(
        (c) =>
          !(
            c.tag === heroCorrelation.tag &&
            c.symptom_type === heroCorrelation.symptom_type
          ),
      )
    : correlations

  return (
    <div
      className="insight-card overflow-hidden rounded-2xl"
      style={{
        background: "var(--fig-card-bg)",
        boxShadow: "var(--fig-card-shadow)",
        border: "1px solid var(--fig-card-border)",
      }}
    >
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700 }}>Other patterns</h3>
          <span style={{ fontSize: 12, color: "var(--fig-text-inactive)" }}>
            Food → symptom patterns
          </span>
        </div>
        <div
          className="flex items-center justify-center"
          style={{
            width: 32,
            height: 32,
            borderRadius: 10,
            background: "var(--fig-tag-bg)",
          }}
        >
          <Link2 size={16} style={{ color: "var(--fig-correlation-arrow)" }} />
        </div>
      </div>

      {remaining.length === 0 ? (
        <div className="px-4 pt-1 pb-4">
          <p style={{ fontSize: 13, color: "var(--fig-text-inactive)" }}>
            Not enough data for patterns yet
          </p>
        </div>
      ) : (
        remaining.map((correlation, idx) => {
          const { bg, color } = getStrengthStyle(correlation.strength)
          const symptomLabel = capitalize(correlation.symptom_type)

          return (
            <div
              key={`${correlation.tag}-${correlation.symptom_type}`}
              className="correlation-card flex items-center gap-3 px-4 py-3"
              style={
                idx < remaining.length - 1
                  ? { borderBottom: "1px solid var(--fig-card-border)" }
                  : {}
              }
            >
              <div
                className="flex shrink-0 items-center justify-center"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: bg,
                }}
              >
                <TrendingUp size={18} style={{ color }} />
              </div>
              <div className="min-w-0 flex-1">
                <p style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>
                  {symptomLabel} followed {correlation.tag}
                </p>
                <p
                  style={{
                    fontSize: 12,
                    color: "var(--fig-text-inactive)",
                    marginTop: 1,
                  }}
                >
                  {correlation.occurrences} of {correlation.total} times
                </p>
              </div>
              <span
                className="flex shrink-0 items-center justify-center rounded-md px-2 py-0.5"
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color,
                  background: bg,
                }}
              >
                {correlation.percentage}%
              </span>
            </div>
          )
        })
      )}
    </div>
  )
}
